import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useRef } from 'react';

import {
  FileStatusButton,
  FileStatusIconCompleted,
  FileStatusSubtitle,
  FileStatusTitle,
} from '@air/feature-file-status/ui';
import { Close } from '@air/next-icons';

import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { type GetCompletedMetadata, type ZippingTask } from '~/store/tasks/types';
import { DownloadFile, isS3DownloadUrlActive } from '~/utils/DownloadFile';

export type ZippingTaskCompletedPanelItemProps = {
  metadata: GetCompletedMetadata<ZippingTask>;
  onClear: () => void;
  onHandleExpiredLink: () => void;
};

export const ZippingTaskCompletedPanelItem = memo(
  ({
    metadata: { url, clipIds = [], boardIds = [] },
    onClear,
    onHandleExpiredLink,
  }: ZippingTaskCompletedPanelItemProps) => {
    const downloadedRef = useRef(false);
    /**
     * By being in ref, it prevents the `download` function from changing
     * and thus not triggering the download `useEffect` below.
     */
    const handleExpiredLinkRef = useRef(onHandleExpiredLink);
    handleExpiredLinkRef.current = onHandleExpiredLink;

    const download = useCallback((url: string) => {
      /**
       * This ref is to ensure the user doesn't start automically downloading the zip again.
       * After they start the download, we don't clear the downloaded item because it's possible
       * their download never started but we don't want to potentially automatically download again
       */
      if (isS3DownloadUrlActive(url) && !downloadedRef.current) {
        DownloadFile(url);
        downloadedRef.current = true;
      } else {
        handleExpiredLinkRef.current();
      }
    }, []);

    useEffect(() => {
      if (url) {
        download(url);
      }
    }, [download, url]);

    return (
      <PaneItemTW
        avatar={<FileStatusIconCompleted />}
        title={
          <FileStatusTitle data-testid="ZIPPING_PANEL_COMPLETED_TITLE">{`Zipped ${pluralize(
            'item',
            clipIds.length + boardIds.length,
            true,
          )}`}</FileStatusTitle>
        }
        subtitle={
          <FileStatusSubtitle className="truncate">
            {
              <>
                {`Download didn't start? `}
                <button
                  className="font-semibold text-12 text-altitude-9 hover:text-altitude-11 hover:underline"
                  onClick={() => {
                    if (url) {
                      download(url);
                    }
                  }}
                  type="button"
                >
                  Click here
                </button>
              </>
            }
          </FileStatusSubtitle>
        }
        buttons={
          <FileStatusButton label="Clear" onClick={() => onClear()}>
            <Close className="size-4" />
          </FileStatusButton>
        }
      />
    );
  },
);

ZippingTaskCompletedPanelItem.displayName = 'ZippingCompletedPanelItem';
